<template>
  <section class="vision-section">
    <div class="vision-container">
      <div class="vision-content">
        <h2 class="vision-title">{{ translations[locale].title }}</h2>
        <p class="vision-subtitle">{{ translations[locale].subtitle }}</p>
        <div class="vision-cards">
          <div class="vision-card">
            <div class="vision-icon">
              <img src="@/assets/img/icon_1.png" alt="decentralization" />
            </div>
            <h3>{{ translations[locale].features.security.title }}</h3>
            <p>{{ translations[locale].features.security.description }}</p>
          </div>
          <div class="vision-card">
            <div class="vision-icon">
              <img src="@/assets/img/icon_2.png" alt="security" />
            </div>
            <h3>{{ translations[locale].features.strategy.title }}</h3>
            <p>{{ translations[locale].features.strategy.description }}</p>
          </div>
          <div class="vision-card">
            <div class="vision-icon">
              <img src="@/assets/img/icon_3.png" alt="scalability" />
            </div>
            <h3>{{ translations[locale].features.performance.title }}</h3>
            <p>{{ translations[locale].features.performance.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from '@/stores/language'

const store = useStore()
const locale = computed(() => store.locale)

const translations = {
  zh: {
    title: '為什麼選擇KOMA',
    subtitle: '我們提供全方位的區塊鏈解決方案,從科技架構到應用場景,為您的數位資產管理提供最佳選擇',
    features: {
      security: {
        title: 'SDR（Special Drawing Rights）',
        description: '是由國際貨幣基金組織（IMF）創設的一種國際儲備資產，用於在國家之間結算支付和增强外匯儲備的穩定性。 其價值由一籃子五種主要貨幣構成（美元、歐元、人民幣、日元、英鎊），具有多幣種錨定、價值穩定的特點。'
      },
      strategy: {
        title: 'RWA（Real World Assets）',
        description: '指将现实中的资产（如债券、房产、黄金、票据）映射或托管到链上，赋予其可编程性与流动性，正在成为 DeFi 下一波的增长引擎。 '
      },
      performance: {
        title: '金融公链（Financial Public Chain）',
        description: '是為數位金融服務而專門設計的區塊鏈網絡，具備高合規性：支持KYC/DID、鏈上信用稽核與監管介面。 高性能：支持高頻、低成本的交易需求（如支付、借貸、清算）。 '
      }
    }
  },
  en: {
    title: 'Why Choose KOMA',
    subtitle: 'We provide comprehensive blockchain solutions, from technical architecture to application scenarios, offering the best choice for your digital asset management',
    features: {
      security: {
        title: 'SDR（Special Drawing Rights）',
        description: 'It is an international reserve asset created by the International Monetary Fund (IMF) to settle payments between countries and enhance the stability of foreign exchange reserves.Its value is composed of a basket of five major currencies(USD, EUR, CNY, JPY, GBP), with the characteristics of multi currency anchoring and stable value.'
      },
      strategy: {
        title: 'RWA（Real World Assets）',
        description: 'Mapping or hosting real-world assets such as bonds, real estate, gold, and bills onto the chain, endowing them with programmability and liquidity, is becoming the next wave of growth engine for DeFi.'
      },
      performance: {
        title: 'Financial Public Chain',
        description: 'It is a blockchain network specifically designed for digital financial services, with high compliance: supporting KYC/DID, on chain credit auditing, and regulatory interfaces. High performance: Supports high-frequency, low-cost transaction needs such as payment, lending, and clearing.'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.vision-section {
  padding: 96px 0;
  background: #F9FAFB;
  position: relative;
}

.vision-container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 32px;
}

.vision-header {
  text-align: center;
  margin-bottom: 48px;
}

.vision-title {
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 700;
  line-height: 1.2;
  color: #F8FAFC;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #60A5FA 0%, #A78BFA 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  // font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  // letter-spacing: 0.05em;
  text-align: center;
}

.vision-subtitle {
  font-size: clamp(0.875rem, 1.5vw, 1rem);
  color: #4B5563;
  line-height: 1.5;
  max-width: 672px;
  margin: 0 auto;
}

.vision-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-top: 48px;
}

.vision-card {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  }
}

.card-icon {
  width: 64px;
  height: 64px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;

  img {
    width: 24px;
    height: 24px;
  }
}

.vision-card h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #6366f1;
  margin-bottom: 1rem;
}

.vision-card p {
  font-size: 1rem;
  color: #4B5563;
  line-height: 1.5;
}

// 响应式设计
@media screen and (max-width: 1024px) {
  .vision-container {
    padding: 0 24px;
  }

  .vision-cards {
    gap: 24px;
  }
}

@media screen and (max-width: 768px) {
  .vision-section {
    padding: 60px 0;
  }

  .vision-cards {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }

  .vision-card {
    padding: 24px;
  }
}

@media screen and (max-width: 480px) {
  .vision-section {
    padding: 40px 0;
  }

  .vision-container {
    padding: 0 16px;
  }

  .vision-card {
    padding: 20px;
  }
}

// 深色模式支持
@media (prefers-color-scheme: dark) {
  .vision-section {
    background: #1F2937;
  }

  .vision-card {
    background: #374151;
  }

  .vision-subtitle {
    color: #9CA3AF;
  }

  .vision-card p {
    color: #9CA3AF;
  }
}
</style>